import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, d as create$7, F as FormController } from './form.js';
import { a as BusinessFormStep, B as BusinessFormStepV2 } from './business-form-types.js';
import { c as config } from './config.js';
import { A as Api } from './Api.js';
import './Checkout.js';
import './Pagination.js';
import './Payment.js';
import './SubAccount.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { d as defineCustomElement$4 } from './form-control-checkbox2.js';
import { d as defineCustomElement$3 } from './form-control-error-text2.js';
import { d as defineCustomElement$2 } from './form-control-help-text2.js';
import { d as defineCustomElement$1 } from './business-terms-conditions-text.js';

const businessTermsConditionsSchema = (allowOptionalFields) => {
    const schema = create$3({
        accepted: create$7()
            .oneOf([true], 'You must agree to the terms and conditions to continue')
            .required('You must agree to the terms and conditions to continue')
    });
    const easySchema = create$3({
        accepted: create$7().nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

const BusinessTermsConditionsFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessTermsConditionsFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get(this.businessEndpoint);
                this.acceptedTermsBefore = response.data.terms_conditions_accepted;
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const payload = this.termsPayload;
                const response = await this.api.post(this.termsConditionsEndpoint, payload);
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.acceptedTermsBefore = undefined;
        this.authToken = undefined;
        this.businessId = undefined;
        this.allowOptionalFields = undefined;
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get termsConditionsEndpoint() {
        return 'entities/terms_and_conditions';
    }
    get formHelperText() {
        return this.acceptedTermsBefore ? 'You have already accepted the terms and conditions.' : null;
    }
    get termsPayload() {
        return {
            business_id: this.businessId,
            accepted: this.formController.values.getValue().accepted,
            user_agent: window.navigator.userAgent
        };
    }
    async componentWillLoad() {
        this.api = Api({ authToken: this.authToken, apiOrigin: config.proxyApiOrigin });
        this.formController = new FormController(businessTermsConditionsSchema(this.allowOptionalFields));
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.submitted.emit({ data: response, metadata: { completedStep: BusinessFormStep.termsAndConditions } });
        this.stepCompleted.emit({ data: response, formStep: BusinessFormStepV2.termsAndConditions });
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.acceptedTermsBefore) {
            this.submitted.emit({ metadata: { completedStep: BusinessFormStep.termsAndConditions } });
            onSuccess();
        }
        else {
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    ;
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        return (h("form", { key: 'e675f117eb90a0690e148a1164fffde5d4f01419' }, h("fieldset", { key: 'e1cb1fe38b7365cc282eb91689afb40d6b01136c' }, h("legend", { key: 'e57a4bc006983c3ee3805445d750ea330bedd477' }, "Terms and Conditions"), h("hr", { key: '7b373693bd10d6493816b3dd9ae07d826bd7ff84' }), h("justifi-business-terms-conditions-text", { key: '984b3668240861df4947c60068c1be079a3eb7e1' }), h("br", { key: '15dfba53dd81d39be4ea216bf798f51881abf23d' }), h("div", { key: 'b426c709c11435911efb4289b68fa8f86c2569f2', class: "row-gy-3" }, h("div", { key: '307c51a7f78613807054a5d1480318fdb9316bff', class: "col-12" }, h("form-control-checkbox", { key: '6069cb9929c7ea0f2c1b2cbc99dad61806c80429', name: "accepted", label: "I agree to the terms and conditions", inputHandler: this.inputHandler, errorText: this.errors.accepted, disabled: this.acceptedTermsBefore, helpText: this.formHelperText }))))));
    }
}, [0, "justifi-business-terms-conditions-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "acceptedTermsBefore": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-terms-conditions-form-step", "form-control-checkbox", "form-control-error-text", "form-control-help-text", "justifi-business-terms-conditions-text"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-terms-conditions-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessTermsConditionsFormStep);
            }
            break;
        case "form-control-checkbox":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "justifi-business-terms-conditions-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessTermsConditionsFormStep as B, defineCustomElement as d };
